import { useRef, useState, useEffect, memo, MouseEventHandler } from 'react';
import UploadPictureExample from './upload-picture-example';
import { getGlobalStore } from '@/store/global';
import { LoadingOutlined } from '@ant-design/icons';
import Cropper from 'cropperjs';
import queryDomMethods from '@/utils/query-dom-methods';
import 'cropperjs/dist/cropper.css';
import './index.scss';
import { recoverBodyScroll, stopBodyScroll } from '@/utils';

const PictureCrop = () => {
    const cropper = useRef<Cropper>();
    const operationContaier = useRef<HTMLDivElement>(null);
    const exampleMaxHeight = useRef('0');
    const callbackRef = useRef<(url: string) => void>();
    const [pictureUrl, setPictureUrl] = useState('');
    const [exampleVisible, setExampleVisible] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleEnlarge = () => cropper.current?.zoom(0.1);

    const handleNarrow = () => cropper.current?.zoom(-0.1);

    const handleTurnLeft = () => {
        cropper.current?.rotate(-90);
        cropper.current?.zoomTo(0);
    };

    const handleTurnRight = () => {
        cropper.current?.rotate(90);
        cropper.current?.zoomTo(0);
    };

    const handleSure = () => {
        if (loading) return;
        setLoading(true);
        cropper.current?.crop();
        cropper.current?.getCroppedCanvas().toBlob(
            blob => {
                if (!blob) return;
                const url = URL.createObjectURL(blob);
                // console.log(`切图大小：${Math.round((blob.size / 1024 / 1024) * 10) / 10}M；切图地址：${url}`);
                handleReset();
                callbackRef.current
                    ? callbackRef.current(url)
                    : getGlobalStore()?.publish('CombinePicturesDrawer', { type: 'crop_success', url });
                setLoading(false);
            },
            'image/jpg',
            0.7
        );
    };

    const handleReset = () => {
        if (loading) return;
        cropper.current?.clear();
        setPictureUrl('');
        setExampleVisible(false);
    };

    const handleControlExample: MouseEventHandler = ev => {
        ev.stopPropagation();
        setExampleVisible(oldVisible => !oldVisible);
    };

    const handleCloseExample = () => setExampleVisible(false);

    useEffect(() => {
        if (!pictureUrl) return;
        if (cropper.current) {
            cropper.current.replace(pictureUrl);
        } else {
            const image = queryDomMethods.querySelector('.picture-crop__picture') as any;
            cropper.current = new Cropper(image, {
                viewMode: 1,
                minCropBoxWidth: 50,
                minCropBoxHeight: 50,
                autoCropArea: 0.96,
                cropBoxMovable: false,
            });
        }
        exampleMaxHeight.current = `${(operationContaier.current?.clientHeight || 0) + 80}px`;
    }, [pictureUrl]);

    useEffect(() => {
        getGlobalStore()?.subscribe('PictureCrop', ({ type, url, callback }) => {
            switch (type) {
                case 'crop':
                    callbackRef.current = callback;
                    setPictureUrl(url);
                    break;
                case 'destroy':
                    cropper.current?.destroy();
                    cropper.current = undefined;
                    break;
                default:
                    break;
            }
        });
    }, []);

    useEffect(() => {
        pictureUrl ? stopBodyScroll() : recoverBodyScroll();
    }, [pictureUrl]);

    return (
        <div className="picture-crop-overlay" style={{ display: pictureUrl === '' ? 'none' : 'block' }} onClick={handleCloseExample}>
            <div className="picture-crop__operation-container" ref={operationContaier}>
                <div className="picture-crop__title">双击图片可移动，拖拽边框可调整选择区</div>
                <div className="picture-crop__picture-container">
                    <img className="picture-crop__picture" src={pictureUrl} />
                </div>
                <div className="picture-crop__operations">
                    <div className="picture-crop__enlarge" onClick={handleEnlarge}></div>
                    <div className="picture-crop__narrow" onClick={handleNarrow}></div>
                    <div className="picture-crop__turn-left" onClick={handleTurnLeft}></div>
                    <div className="picture-crop__turn-right" onClick={handleTurnRight}></div>
                    <div className="picture-crop__line"></div>
                    <div className="picture-crop__cancel" onClick={handleReset}>
                        取消
                    </div>
                    <div className="picture-crop__sure" onClick={handleSure}>
                        {loading ? <LoadingOutlined /> : '确定'}
                    </div>
                </div>
                <div className="picture-crop__example-btn" onClick={handleControlExample}>
                    <span>示例</span>
                    <UploadPictureExample
                        visible={exampleVisible}
                        maxHeight={exampleMaxHeight.current}
                        title="拍照示例"
                        onClose={handleControlExample}
                        showMode="heightChange"
                        className="picture-crop__example"
                    />
                </div>
            </div>
        </div>
    );
};

export default memo(PictureCrop);
